import React, {useMemo} from 'react';
import {Box, Button, Grid, Stack, Typography} from '@mui/material';
import type {ButtonProps} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {Concept} from 'types';
import type {NoProps} from 'types';
import type {TextVariant} from 'theme/types';
import {
  ButtonWithPopupMenu,
  CancelButton,
  Card,
  ConfirmButton,
  ControlledIconButton,
  CopyToClipboard,
  FlexBox,
  Icon,
  IconButton,
  Link,
  MenuButton,
  NavigationButton,
  SaveButton,
  Scene,
  Spacer,
  SpanButton,
  Text,
  ToolButton,
} from 'components';
import {ActionButtonV2} from 'components/button/ActionButtonV2';
import {DeleteButtonV2} from 'components/button/DeleteButtonV2';
import {UnstyledButton} from 'components/button/UnstyledButton';
import {AddButton} from 'components/fields/AddButton';
import {ListActionButton} from 'components/fields/ListActionButton';
import {EntityButton} from 'feature/linkedValueSearch/components/EntityButton';

type Props = NoProps;

/**
 * Typography variants showcased on the page, in display order. Each maps to a
 * MUI typography variant that {@link Text} can render via its `variant` prop.
 */
const typographyVariants: {variant: TextVariant; description: string}[] = [
  {variant: 'h1', description: 'Page title'},
  {variant: 'h2', description: 'Section / card title'},
  {variant: 'h3', description: 'Sub-section (uppercase weight 700)'},
  {variant: 'h4', description: 'Minor heading'},
  {variant: 'subtitle1', description: 'Subtitle'},
  {variant: 'body1', description: 'Default body text'},
  {variant: 'body2', description: 'Dense body / input text'},
  {variant: 'caption', description: 'Captions and labels'},
  {variant: 'button', description: 'Button label'},
];

const sampleText = 'The quick brown fox jumps over the lazy dog';

/**
 * Pick a readable text color (near-black or near-white) for a given background
 * hex, using the standard relative-luminance threshold.
 */
const getContrastText = (
  hex: string,
  darkText: string,
  lightText: string,
): string => {
  const normalized = hex.replace('#', '').slice(0, 6);
  if (normalized.length < 6) {
    return darkText;
  }
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? darkText : lightText;
};

type Swatch = {name: string; value: string};

/** Flatten a record of colors (string or nested record) into named swatches. */
const toSwatches = (colors: Record<string, unknown>, prefix = ''): Swatch[] =>
  Object.entries(colors).flatMap(([key, value]) => {
    const name = prefix ? `${prefix}.${key}` : key;
    if (typeof value === 'string') {
      return [{name, value}];
    }
    if (value && typeof value === 'object') {
      return toSwatches(value as Record<string, unknown>, name);
    }
    return [];
  });

const ColorSwatch: React.FC<Swatch> = ({name, value}) => {
  const {palette, colors} = useTheme();
  const contrast = getContrastText(
    value,
    palette.text.primary,
    colors.offWhite,
  );
  return (
    <Box
      sx={{
        backgroundColor: value,
        color: contrast,
        borderRadius: 1,
        border: `1px solid ${colors.warmGreen06}`,
        p: 1.5,
        height: 84,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
      }}>
      <Typography variant="body2" sx={{color: contrast, fontWeight: 700}}>
        {name}
      </Typography>
      <Typography
        variant="caption"
        sx={{color: contrast, textTransform: 'uppercase'}}>
        {value}
      </Typography>
    </Box>
  );
};

const SwatchGroup: React.FC<{title: string; swatches: Swatch[]}> = ({
  title,
  swatches,
}) => (
  <>
    <Spacer size={1} />
    <Text variant="h3" kind="primary">
      {title}
    </Text>
    <Spacer size={1} />
    <Grid container spacing={1.5}>
      {swatches.map(swatch => (
        <Grid key={swatch.name} size={{xs: 6, sm: 4, md: 3}}>
          <ColorSwatch {...swatch} />
        </Grid>
      ))}
    </Grid>
    <Spacer size={2} />
  </>
);

type BackgroundLevels = {level1: string; level2: string; level3: string};

/**
 * A single labelled background panel. Nests its children (the next, deeper
 * level) inside, so a `beige`/`brown`/`burgundy` family renders as panels
 * within panels going level1 → level2 → level3.
 */
const BackgroundPanel: React.FC<{
  level: string;
  value: string;
  children?: React.ReactNode;
}> = ({level, value, children}) => {
  const {palette, colors} = useTheme();
  const contrast = getContrastText(
    value,
    palette.text.primary,
    colors.offWhite,
  );
  return (
    <Box
      sx={{
        backgroundColor: value,
        borderRadius: 2,
        p: 2,
        border: `1px solid ${colors.warmGreen06}`,
      }}>
      <FlexBox horizontal sx={{justifyContent: 'space-between'}}>
        <Typography variant="body2" sx={{color: contrast, fontWeight: 700}}>
          {level}
        </Typography>
        <Typography
          variant="caption"
          sx={{color: contrast, textTransform: 'uppercase'}}>
          {value}
        </Typography>
      </FlexBox>
      {children && <Spacer size={1.5} />}
      {children}
    </Box>
  );
};

/**
 * Renders one background family as nested panels, demonstrating how the three
 * levels stack to create depth when panels are placed within panels.
 */
const BackgroundFamily: React.FC<{title: string; levels: BackgroundLevels}> = ({
  title,
  levels,
}) => (
  <Box>
    <Text variant="h3" kind="primary">
      {title}
    </Text>
    <Spacer size={1} />
    <BackgroundPanel level="level1" value={levels.level1}>
      <BackgroundPanel level="level2" value={levels.level2}>
        <BackgroundPanel level="level3" value={levels.level3} />
      </BackgroundPanel>
    </BackgroundPanel>
  </Box>
);

/** No-op click handler for the static button examples. */
const noop = (): void => undefined;

/**
 * The MUI Button `color` options available in the theme palette. The app gives
 * contained primary / success / error custom backgrounds; secondary / info /
 * warning fall back to MUI defaults derived from their palette `main` color.
 */
const buttonColors: NonNullable<ButtonProps['color']>[] = [
  'primary',
  'secondary',
  'success',
  'error',
  'info',
  'warning',
  'inherit',
];

/** Resolves after a short delay so spinner-on-click buttons can be observed. */
const simulateRequest = (): Promise<void> =>
  new Promise<void>(resolve => setTimeout(resolve, 1500));

/**
 * A titled group of related button examples, separated by dividers, mirroring
 * the {@link SwatchGroup} pattern used for the palette.
 */
const DemoGroup: React.FC<{title: string; children: React.ReactNode}> = ({
  title,
  children,
}) => (
  <>
    <Spacer size={1} />
    <Text variant="h3" kind="primary">
      {title}
    </Text>
    <Spacer size={1.5} />
    <Stack spacing={2.5} divider={<Divider />}>
      {children}
    </Stack>
    <Spacer size={2} />
  </>
);

/**
 * A single button example: a name (the prop/variant being shown), the live
 * button(s), and a short description of when it is used.
 */
const ButtonDemo: React.FC<{
  name: string;
  description: string;
  children: React.ReactNode;
}> = ({name, description, children}) => (
  <Box>
    <Text variant="caption" kind="secondary">
      {name}
    </Text>
    <Spacer size={0.75} />
    <FlexBox horizontal sx={{gap: 1.5, flexWrap: 'wrap', alignItems: 'center'}}>
      {children}
    </FlexBox>
    <Spacer size={0.75} />
    <Text variant="caption" kind="secondary">
      {description}
    </Text>
  </Box>
);

export const ThemeShowcaseScene: React.FC<Props> = () => {
  const theme = useTheme();
  const {palette, colors, status} = theme;

  const baseSwatches = useMemo(
    () =>
      toSwatches({
        burgundy: colors.burgundy,
        burgundy75: colors.burgundy75,
        burgundy50: colors.burgundy50,
        warmGreen: colors.warmGreen,
        terracotta: colors.terracotta,
        offWhite: colors.offWhite,
        beige: colors.beige,
        brown: colors.brown,
        lightBeige: colors.lightBeige,
        textPrimary: palette.text.primary,
        textSecondary: palette.text.secondary,
      }),
    [colors, palette.text.primary, palette.text.secondary],
  );

  const semanticSwatches = useMemo(
    () =>
      toSwatches({
        info: colors.info,
        success: palette.success.main,
        approve: colors.approve,
        reject: colors.reject,
        warning: palette.warning.main,
        error: palette.error.main,
      }),
    [colors, palette.success.main, palette.warning.main, palette.error.main],
  );

  const statusSwatches = useMemo(() => toSwatches(status), [status]);

  return (
    <Scene browserTitle={'Theme'}>
      <Scene.Header title={'Fargepalett'} />
      <Scene.Content>
        <Grid container spacing={2}>
          {/* Typography */}
          <Grid size={{xs: 12, md: 7}}>
            <Card level="level3">
              <Card.Header>
                <Text variant="h2" kind="primary">
                  {'Typography'}
                </Text>
              </Card.Header>
              <Card.Content>
                <Stack spacing={2.5} divider={<Divider />}>
                  {typographyVariants.map(({variant, description}) => {
                    const style = theme.typography[variant];
                    return (
                      <Box key={variant}>
                        <FlexBox
                          horizontal
                          sx={{justifyContent: 'space-between'}}>
                          <Text variant="caption" kind="secondary">
                            {variant}
                          </Text>
                          <Text variant="caption" kind="secondary">
                            {`${String(style.fontSize ?? '')} · ${String(
                              style.fontWeight ?? '',
                            )} · lh ${String(style.lineHeight ?? '')}`}
                          </Text>
                        </FlexBox>
                        <Spacer size={0.5} />
                        <Text variant={variant} kind="primary">
                          {sampleText}
                        </Text>
                        <Text variant="caption" kind="secondary">
                          {description}
                        </Text>
                      </Box>
                    );
                  })}
                </Stack>
              </Card.Content>
            </Card>
          </Grid>

          {/* Palette */}
          <Grid size={{xs: 12, md: 5}}>
            <Card level="level3">
              <Card.Header>
                <Text variant="h2" kind="primary">
                  {'Palette'}
                </Text>
              </Card.Header>
              <Card.Content>
                <SwatchGroup title="Brand & neutrals" swatches={baseSwatches} />
                <SwatchGroup title="Semantic" swatches={semanticSwatches} />
                <SwatchGroup title="Status" swatches={statusSwatches} />
              </Card.Content>
            </Card>
          </Grid>

          {/* Backgrounds */}
          <Grid size={{xs: 12}}>
            <Card level="level3">
              <Card.Header>
                <Text variant="h2" kind="primary">
                  {'Backgrounds'}
                </Text>
              </Card.Header>
              <Card.Content>
                <Text variant="body2" kind="secondary">
                  {
                    'Each family defines three background levels. Nest panels within panels to build depth.'
                  }
                </Text>
                <Spacer size={2} />
                <Grid container spacing={2}>
                  {(['beige', 'brown', 'burgundy'] as const).map(name => (
                    <Grid key={name} size={{xs: 12, md: 4}}>
                      <BackgroundFamily
                        title={name}
                        levels={palette.background[name]}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Card.Content>
            </Card>
          </Grid>

          {/* Buttons */}
          <Grid size={{xs: 12}}>
            <Card level="level3">
              <Card.Header>
                <Text variant="h2" kind="primary">
                  {'Buttons'}
                </Text>
              </Card.Header>
              <Card.Content>
                <Text variant="body2" kind="secondary">
                  {
                    'The app wraps MUI Button / IconButton / ButtonBase in a set of purpose-built components. Buttons configured with showSpinnerOnClick swap their label or icon for a spinner while the async click handler resolves — click them to see it.'
                  }
                </Text>

                {/* MUI Button (used directly in a few places) */}
                <DemoGroup title="MUI Button (base)">
                  <ButtonDemo
                    name="variant"
                    description="contained · outlined · text — used directly in dialogs (AreYouSureDialog) and toggles (ColumnPicker, ChevronToggle).">
                    <Button variant="contained" onClick={noop}>
                      Contained
                    </Button>
                    <Button variant="outlined" onClick={noop}>
                      Outlined
                    </Button>
                    <Button variant="text" onClick={noop}>
                      Text
                    </Button>
                  </ButtonDemo>
                  <ButtonDemo
                    name="color · contained"
                    description="All palette colors. The app customizes contained primary (off-white), success (green) and error (brown); secondary / info / warning use MUI defaults.">
                    {buttonColors.map(color => (
                      <Button
                        key={color}
                        variant="contained"
                        color={color}
                        onClick={noop}>
                        {color}
                      </Button>
                    ))}
                  </ButtonDemo>
                  <ButtonDemo
                    name="color · outlined"
                    description="The same palette colors as outlined buttons (border + text take the color, no fill).">
                    {buttonColors.map(color => (
                      <Button
                        key={color}
                        variant="outlined"
                        color={color}
                        onClick={noop}>
                        {color}
                      </Button>
                    ))}
                  </ButtonDemo>
                  <ButtonDemo
                    name="color · text"
                    description="The same palette colors as text buttons (color applies to the label only).">
                    {buttonColors.map(color => (
                      <Button
                        key={color}
                        variant="text"
                        color={color}
                        onClick={noop}>
                        {color}
                      </Button>
                    ))}
                  </ButtonDemo>
                  <ButtonDemo
                    name="color · disabled"
                    description="Each palette color in its disabled state.">
                    {buttonColors.map(color => (
                      <Button
                        key={color}
                        variant="contained"
                        color={color}
                        disabled
                        onClick={noop}>
                        {color}
                      </Button>
                    ))}
                  </ButtonDemo>
                  <ButtonDemo name="size" description="small · medium · large.">
                    <Button variant="contained" size="small" onClick={noop}>
                      Small
                    </Button>
                    <Button variant="contained" size="medium" onClick={noop}>
                      Medium
                    </Button>
                    <Button variant="contained" size="large" onClick={noop}>
                      Large
                    </Button>
                  </ButtonDemo>
                  <ButtonDemo
                    name="startIcon"
                    description="A leading icon — the pattern Save/Delete buttons build on.">
                    <Button
                      variant="contained"
                      startIcon={<Icon icon="Add" />}
                      onClick={noop}>
                      Add
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Icon icon="Download" />}
                      onClick={noop}>
                      Download
                    </Button>
                  </ButtonDemo>
                </DemoGroup>

                {/* ActionButtonV2 family — MUI Button + async spinner */}
                <DemoGroup title="ActionButtonV2 family (MUI Button + async spinner)">
                  <ButtonDemo
                    name="ActionButtonV2"
                    description="Wraps MUI Button; accepts an async onClick and shows MUI's loading spinner while it resolves when showSpinnerOnClick is set.">
                    <ActionButtonV2
                      variant="contained"
                      showSpinnerOnClick
                      onClick={simulateRequest}>
                      Click me
                    </ActionButtonV2>
                  </ButtonDemo>
                  <ButtonDemo
                    name="SaveButton"
                    description="ActionButtonV2 preset: success color + Check icon, spinner on click.">
                    <SaveButton variant="contained" onClick={simulateRequest} />
                  </ButtonDemo>
                  <ButtonDemo
                    name="CancelButton"
                    description="ActionButtonV2 preset: primary color, localized default label.">
                    <CancelButton variant="outlined" onClick={noop} />
                  </ButtonDemo>
                  <ButtonDemo
                    name="DeleteButtonV2"
                    description="ActionButtonV2 preset: error color + Delete icon, spinner on click.">
                    <DeleteButtonV2
                      variant="contained"
                      onClick={simulateRequest}
                    />
                  </ButtonDemo>
                </DemoGroup>

                {/* ActionButtonV2 — props in detail */}
                <DemoGroup title="ActionButtonV2 (props in detail)">
                  <ButtonDemo name="size" description="small · medium · large.">
                    <ActionButtonV2
                      variant="contained"
                      size="large"
                      onClick={noop}>
                      Large
                    </ActionButtonV2>
                    <ActionButtonV2
                      variant="contained"
                      size="small"
                      onClick={noop}>
                      Small
                    </ActionButtonV2>
                  </ButtonDemo>
                  <ButtonDemo
                    name="icon + color"
                    description="Optional leading icon (icon prop); color accepts any MUI palette color.">
                    <ActionButtonV2
                      variant="contained"
                      icon="Edit"
                      onClick={noop}>
                      With icon
                    </ActionButtonV2>
                    <ActionButtonV2
                      variant="contained"
                      icon="Star"
                      color="secondary"
                      onClick={noop}>
                      Secondary
                    </ActionButtonV2>
                  </ButtonDemo>
                  <ButtonDemo
                    name="showSpinnerOnClick"
                    description="Shows MUI's loading spinner while the async onClick resolves.">
                    <ActionButtonV2
                      variant="contained"
                      icon="Send"
                      showSpinnerOnClick
                      onClick={simulateRequest}>
                      Submit
                    </ActionButtonV2>
                  </ButtonDemo>
                  <ButtonDemo
                    name="DeleteButtonV2"
                    description="ActionButtonV2 preset: error color + Delete icon + spinner, localized default label.">
                    <DeleteButtonV2
                      variant="contained"
                      onClick={simulateRequest}
                    />
                  </ButtonDemo>
                  <ButtonDemo
                    name="disabled"
                    description="Non-interactive, dimmed disabled state.">
                    <ActionButtonV2 variant="contained" disabled onClick={noop}>
                      Disabled
                    </ActionButtonV2>
                  </ButtonDemo>
                </DemoGroup>

                {/* Icon buttons — MUI IconButton */}
                <DemoGroup title="Icon buttons (MUI IconButton)">
                  <ButtonDemo
                    name="IconButton"
                    description="Icon-only button; shows an activity indicator if the click handler is slow to resolve.">
                    <IconButton
                      icon="Edit"
                      tooltip="Edit"
                      onClick={simulateRequest}
                    />
                    <IconButton
                      icon="Delete"
                      tooltip="Delete"
                      onClick={simulateRequest}
                    />
                  </ButtonDemo>
                  <ButtonDemo name="size" description="small · medium · large.">
                    <IconButton icon="Settings" size="small" onClick={noop} />
                    <IconButton icon="Settings" size="medium" onClick={noop} />
                    <IconButton icon="Settings" size="large" onClick={noop} />
                  </ButtonDemo>
                  <ButtonDemo
                    name="badgeCount / disabled"
                    description="Optional badge overlay; disabled state dims the icon.">
                    <IconButton icon="Comments" badgeCount={3} onClick={noop} />
                    <IconButton icon="Comments" disabled onClick={noop} />
                  </ButtonDemo>
                  <ButtonDemo
                    name="ControlledIconButton"
                    description="Lower-level variant where the calling / delayed (spinner) state is controlled by the parent.">
                    <ControlledIconButton
                      icon="Save"
                      isCalling={false}
                      onClick={noop}
                    />
                    <ControlledIconButton
                      icon="Save"
                      isCalling
                      isDelayed
                      onClick={noop}
                    />
                  </ButtonDemo>
                </DemoGroup>

                {/* ToolButton — round labelled pill with badge / tag */}
                <DemoGroup title="ToolButton (labelled pill, badge & tag)">
                  <ButtonDemo
                    name="icon + label"
                    description="Round pill with a leading icon and capitalized label.">
                    <ToolButton title="Preview" icon="Preview" onClick={noop} />
                    <ToolButton title="History" icon="History" onClick={noop} />
                  </ButtonDemo>
                  <ButtonDemo
                    name="checked"
                    description="Adds a burgundy border to indicate a selected / active state.">
                    <ToolButton
                      title="Selected"
                      icon="Check"
                      checked
                      onClick={noop}
                    />
                  </ButtonDemo>
                  <ButtonDemo
                    name="badgeCount / tag"
                    description="Badge count and a colored chip tag.">
                    <ToolButton
                      title="Comments"
                      icon="Comments"
                      badgeCount={5}
                      onClick={noop}
                    />
                    <ToolButton
                      title="Status"
                      tag="Draft"
                      tagColor="beige"
                      onClick={noop}
                    />
                  </ButtonDemo>
                  <ButtonDemo
                    name="iconOnly"
                    description="Compact 48px round button with no label.">
                    <ToolButton
                      title="More"
                      icon="More"
                      iconOnly
                      onClick={noop}
                    />
                  </ButtonDemo>
                  <ButtonDemo
                    name="ConfirmButton"
                    description="ToolButton that opens an “are you sure?” dialog before invoking onConfirm.">
                    <ConfirmButton
                      title="Delete"
                      icon="Delete"
                      onConfirm={noop}
                    />
                  </ButtonDemo>
                </DemoGroup>

                {/* Navigational */}
                <DemoGroup title="Navigational">
                  <ButtonDemo
                    name="NavigationButton"
                    description="Clickable text (optionally with icon) used for in-page navigation.">
                    <NavigationButton
                      title="Go back"
                      icon="Back"
                      onClick={noop}
                    />
                  </ButtonDemo>
                  <ButtonDemo
                    name="MenuButton"
                    description="Router link styled as a menu item; bordered when selected.">
                    <MenuButton title="Works" path="/theme" selected={false} />
                    <MenuButton title="Agents" path="/theme" selected />
                  </ButtonDemo>
                  <ButtonDemo
                    name="Link"
                    description="MUI Link wired to the router, rendering themed Text.">
                    <Link to="/theme" title="A themed link" />
                  </ButtonDemo>
                </DemoGroup>
              </Card.Content>
            </Card>
          </Grid>

          {/* Composite buttons — one-off assemblies of the primitives above */}
          <Grid size={{xs: 12}}>
            <Card level="level3">
              <Card.Header>
                <Text variant="h2" kind="primary">
                  {'Composite buttons (one-off)'}
                </Text>
              </Card.Header>
              <Card.Content>
                <Text variant="body2" kind="secondary">
                  {
                    'Purpose-built assemblies of the primitives above, each tied to a specific spot in the app rather than meant for general reuse.'
                  }
                </Text>

                <DemoGroup title="Field actions">
                  <ButtonDemo
                    name="AddButton"
                    description="An Add icon + label as two small MUI IconButtons; used to add items to a list (e.g. Notes).">
                    <AddButton label="Add note" onClick={noop} />
                  </ButtonDemo>
                  <ButtonDemo
                    name="ListActionButton"
                    description="Icon + label list action; iconPosition left/right, and recolors to error/warning when the field has an error.">
                    <ListActionButton
                      icon="Add"
                      label="Add row"
                      onClick={noop}
                    />
                    <ListActionButton
                      icon="Delete"
                      iconPosition="right"
                      label="Remove"
                      onClick={noop}
                    />
                    <ListActionButton
                      icon="Add"
                      label="Required"
                      error={true}
                      onClick={noop}
                    />
                  </ButtonDemo>
                </DemoGroup>

                <DemoGroup title="Search / selection">
                  <ButtonDemo
                    name="EntityButton"
                    description="Pill used in linked-value search to pick an entity type; bordered when selected, with an optional count or loading badge.">
                    <EntityButton type={Concept.work} isSelected={false} />
                    <EntityButton type={Concept.agent} isSelected={true} />
                    <EntityButton
                      type={Concept.collection}
                      isSelected={false}
                      count={12}
                    />
                    <EntityButton
                      type={Concept.prize}
                      isSelected={false}
                      loading
                    />
                  </ButtonDemo>
                </DemoGroup>

                <DemoGroup title="Menus & utilities">
                  <ButtonDemo
                    name="ButtonWithPopupMenu"
                    description="IconButton (optionally with a label) that opens a PopupMenu of actions.">
                    <ButtonWithPopupMenu
                      icon="More"
                      items={[
                        {id: 'edit', title: 'Edit', icon: 'Edit', action: noop},
                        {
                          id: 'delete',
                          title: 'Delete',
                          icon: 'Delete',
                          action: noop,
                        },
                      ]}
                    />
                    <ButtonWithPopupMenu
                      icon="Menu"
                      title="Actions"
                      items={[
                        {id: 'a', title: 'First action', action: noop},
                        {type: 'divider'},
                        {id: 'b', title: 'Second action', action: noop},
                      ]}
                    />
                  </ButtonDemo>
                  <ButtonDemo
                    name="CopyToClipboard"
                    description="IconButton that copies the given text to the clipboard.">
                    <CopyToClipboard text="Copied from the theme showcase" />
                  </ButtonDemo>
                </DemoGroup>

                <DemoGroup title="Fallbacks">
                  <ButtonDemo
                    name="SpanButton"
                    description="A styled div (not a <button>) — last resort for clickable content nested inside another button, e.g. an accordion header.">
                    <SpanButton onClick={noop}>Span button</SpanButton>
                  </ButtonDemo>
                  <ButtonDemo
                    name="UnstyledButton"
                    description="Bare <button> with stop-propagation; the base for NavigationButton.">
                    <UnstyledButton onClick={noop}>
                      <Text kind="primary" variant="button">
                        Unstyled button
                      </Text>
                    </UnstyledButton>
                  </ButtonDemo>
                </DemoGroup>
              </Card.Content>
            </Card>
          </Grid>
        </Grid>
      </Scene.Content>
    </Scene>
  );
};

const Divider: React.FC = () => {
  const {colors} = useTheme();
  return <Box sx={{borderTop: `1px solid ${colors.beige}`}} />;
};
